import type { IconComponent, IconProps } from '@consta/icons/Icon'
import { IconAlert } from '@consta/icons/IconAlert'
import { IconAllDone } from '@consta/icons/IconAllDone'
import { IconDocBlank } from '@consta/icons/IconDocBlank'
import { IconPause } from '@consta/icons/IconPause'
import { IconTrash } from '@consta/icons/IconTrash'
import { Button } from '@consta/uikit/Button'
import { Card } from '@consta/uikit/Card'
import { Checkbox } from '@consta/uikit/Checkbox'
import { Tag } from '@consta/uikit/Tag'
import { Text } from '@consta/uikit/Text'
import { Timer } from '@consta/uikit/Timer'
import { withTooltip } from '@consta/uikit/withTooltip'
import { getSimpleTooltipContent } from '@core/core/helpers'
import {
  selectActiveMultipartFile,
  selectMultipartConvertProgress,
  selectMultipartProgress,
  useAppSelector,
} from '@core/core/store'
import type { MultipartFileStatusType, UploadedFileStepType } from '@core/core/types'
import type { Nullable } from 'common/types'
import type { ChangeEventHandler, FC, MouseEvent } from 'react'

import type { FilesCardPropsType } from '../types'
import css from './FileImport.css'
import { ProgressStepBar } from './Steps'
import type { TagData } from './types'
import { useFilesCard } from './useFilesCard'

const lineHeight = { lineHeight: '24px' }

const statusTagsData: TagData = {
  ready: { label: 'Готово', group: 6 },
  loading: { label: 'Загрузка', group: 1 },
  conversion: { label: 'Конвертация', group: 9 },
  setup: { label: 'Настройка', group: 1 },
  mapping: { label: 'Маппинг', group: 1 },
  timer: { label: '', group: 1 },
  pause: { label: '', group: 1 },
  error: { label: '', group: 1 },
  default: { label: '', group: 1 },
}

const iconsStatusData: Record<MultipartFileStatusType | 'default', Nullable<IconComponent>> = {
  ready: (props: IconProps) => <IconAllDone {...props} view="success" />,
  paused: (props: IconProps) => <IconPause {...props} view="alert" />,
  failed: (props: IconProps) => <IconAlert {...props} view="alert" />,
  default: (props: IconProps) => <IconDocBlank {...props} view="ghost" />,
  idle: (props: IconProps) => <IconDocBlank {...props} view="ghost" />,
  succeeded: (props: IconProps) => <IconDocBlank {...props} view="ghost" />,
  inprogress: (props: IconProps) => <IconDocBlank {...props} view="ghost" />,
  notstarted: (props: IconProps) => <IconDocBlank {...props} view="ghost" />,
  addedToProject: (props: IconProps) => <IconDocBlank {...props} view="ghost" />,
  excluded: null,
}

const TextFileNameWithTooltip = withTooltip({
  direction: 'rightCenter',
  size: 's',
})(Text)

export const FilesCard: FC<FilesCardPropsType> = ({
  file,
  selected,
  removeCard,
  onMasterImportOpen,
  onSelectCard,
}) => {
  const id = file.fileId ?? ''
  const taskId = file.uploadedFile?.convertTaskId
  const name = file.uploadedFile?.name
  const date = file.uploadedFile?.date ? new Date(file.uploadedFile.date).toLocaleString() : ''
  const activeFile = useAppSelector(selectActiveMultipartFile)
  const filesCardClassName = activeFile?.fileId === id ? css.activeFileCard : css.filesCard
  const progress = Math.floor(
    useAppSelector(state => selectMultipartProgress(state, file.fileId)) ?? 0,
  )
  const convertProgress = Math.floor(
    useAppSelector(state => selectMultipartConvertProgress(state, file.fileId)) ?? 0,
  )
  const { step, statuses } = useFilesCard(id)
  const statusTagData = statusTagsData[step]
  const activeStep: UploadedFileStepType = file.uploadedFile?.step ?? 'setup'
  const activeStatus: MultipartFileStatusType =
    file.uploadedFile?.statuses?.[activeStep] ?? 'notstarted'
  const IconStatusData: Nullable<IconComponent> =
    iconsStatusData[activeStatus] ?? iconsStatusData.default

  const onChangeChecked: ChangeEventHandler<HTMLInputElement> = event => {
    if (id) {
      onSelectCard(id, event.target.checked)
    }
  }

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation()

    if (!id) return

    removeCard(id, taskId)
  }

  const stepIndicator = () => {
    if (activeStatus === 'failed') {
      return <IconAlert size="s" className={css.statusIcon} view="alert" />
    }

    switch (activeStep) {
      case 'loading': {
        return <Timer size="s" seconds={progress} progress={progress} className={css.timer} />
      }
      case 'conversion': {
        return (
          <Timer
            size="s"
            seconds={convertProgress}
            progress={convertProgress}
            className={css.timer}
          />
        )
      }
      case 'ready':
      case 'setup':
      case 'mapping':
        break
    }
    return IconStatusData ? <IconStatusData size="s" className={css.statusIcon} /> : null
  }
  return (
    <Card
      className={filesCardClassName}
      horizontalSpace="s"
      verticalSpace="s"
      form="round"
      shadow={false}
      onClick={() => {
        onMasterImportOpen(id)
      }}>
      <div className={css.blockWrapper}>
        {Object.entries(statuses ?? {})
          .filter(([, value]) => value !== 'excluded')
          .map(([key, value]) => (
            <ProgressStepBar key={key} step={step} status={value} />
          ))}
      </div>

      <div className={css.blockWrapper}>
        <Text size="xs" view="secondary" style={lineHeight}>
          {date}
        </Text>
        <Button
          size="xs"
          view="clear"
          onlyIcon={true}
          iconRight={() => <IconTrash size="xs" view="ghost" />}
          onClick={handleDelete}
        />
      </div>

      <div className={css.stepIndicatorWrapper}>
        <Checkbox
          className={css.stepIndicatorCheck}
          checked={selected}
          onChange={onChangeChecked}
        />
        <div>{stepIndicator()}</div>

        <TextFileNameWithTooltip
          size="s"
          view="primary"
          weight="semibold"
          style={lineHeight}
          truncate={true}
          tooltipProps={{ content: getSimpleTooltipContent(name ?? '') }}>
          {name}
        </TextFileNameWithTooltip>
      </div>

      <div className={css.tagWrapper}>
        {!statusTagData.label && (
          <Tag size="xs" mode="info" label={statusTagData.label} group={statusTagData.group} />
        )}
      </div>
    </Card>
  )
}
